/**
 * Every lead, all the way back.
 *
 * Today is scoped to the current day by design, which left the console with no
 * way to answer "did that enquiry from last Tuesday ever go out?". This screen
 * is that answer: a paged, filterable view of `pipeline_leads` with each lead's
 * delivery outcome beside it.
 *
 * Paging and filtering are both SERVER side. The table only grows, so a client
 * that pulled everything and filtered in memory would work for a month and then
 * quietly become the slowest screen in the product.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  ArrowCounterClockwiseIcon,
  MagnifyingGlassIcon,
  TrayIcon,
  XIcon,
} from "@phosphor-icons/react";
import {
  EMPTY_LEAD_FILTERS,
  LEAD_PAGE_SIZE,
  loadLeadPage,
  siteName,
  sites,
  type LeadFilters,
  type LeadWithDeliveries,
  type Paged,
} from "../lib/data.ts";
import { useAsync, useDebounced, useDebouncedValue } from "../lib/useAsync.ts";
import { listTime } from "../lib/format.ts";
import {
  Badge,
  Button,
  Chevron,
  EmptyState,
  ErrorState,
  Pager,
  Panel,
  RowSkeleton,
  SkeletonReveal,
  type Tone,
} from "../components/ui.tsx";
import { INTENT_LABEL, LeadDetail, deliveryOutcome } from "../components/LeadDetail.tsx";
import { useLiveRefresh } from "../lib/live.ts";
import { QUALITY_LEVELS, ROUTING_STATUSES } from "@pipeline/types.ts";
import type { LeadRecord } from "@pipeline/types.ts";

/* ------------------------------------------------------------------ status */

function leadStatus(lead: LeadRecord): { tone: Tone; label: string } {
  if (lead.quality === "spam") return { tone: "bad", label: "spam" };
  if (lead.isDuplicate) return { tone: "neutral", label: "duplicate" };
  if (lead.routingStatus === "held") return { tone: "warn", label: "held" };
  if (lead.routingStatus === "skipped") return { tone: "neutral", label: "skipped" };
  if (lead.routingStatus === "routed") return { tone: "ok", label: "forwarded" };
  return { tone: "accent", label: "pending" };
}

/* ------------------------------------------------------------------ screen */

const QUALITY_OPTIONS = [
  { value: "", label: "Any quality" },
  ...QUALITY_LEVELS.map((level) => ({ value: level, label: level })),
];

const ROUTING_OPTIONS = [
  { value: "", label: "Any status" },
  ...ROUTING_STATUSES.map((status) => ({ value: status, label: status })),
];

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (next: string) => void;
}) {
  return (
    <span className="select-wrap" data-active={value !== ""}>
      <select
        className="select"
        data-active={value !== ""}
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className="select-wrap__chevron">
        <Chevron size={14} />
      </span>
    </span>
  );
}

export function Leads() {
  const [filters, setFilters] = useState<LeadFilters>(EMPTY_LEAD_FILTERS);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  // The search box types at human speed; the server should not.
  const search = useDebouncedValue(filters.search, 300);

  const state = useAsync<Paged<LeadWithDeliveries>>(
    () => loadLeadPage({ ...filters, search }, page),
    [search, filters.siteKey, filters.quality, filters.routingStatus, page],
  );

  // Only page one follows the live pipeline. Refetching page six because a new
  // lead landed would shift every row under the reader's cursor.
  const pageRef = useRef(page);
  pageRef.current = page;
  const refresh = useDebounced(() => {
    if (pageRef.current === 1) state.reload();
  }, 800);
  const connected = useLiveRefresh(refresh);

  const update = useCallback((patch: Partial<LeadFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    // A filter that narrows the set while you are on page four would otherwise
    // land you on an empty page.
    setPage(1);
    setOpenId(null);
  }, []);

  const anyFilter =
    filters.search !== "" ||
    filters.siteKey !== "" ||
    filters.quality !== "" ||
    filters.routingStatus !== "";

  const siteOptions = useMemo(
    () => [
      { value: "", label: "Every site" },
      ...sites.map((site) => ({ value: site.key, label: site.name })),
    ],
    [],
  );

  if (state.error && !state.data) {
    return <ErrorState message={state.error} onRetry={state.reload} />;
  }

  const result = state.data;
  const rows = result?.items ?? [];
  // `loading` is true on every filter and page change, but the previous page is
  // still in `data`. Skeletons belong to the genuine first load; a filter change
  // dims the list it already has, which reads as the same list narrowing rather
  // than the screen being rebuilt.
  const firstLoad = state.loading && !result;
  const busy = state.loading && Boolean(result);

  return (
    <div className="stack stack--5">
      <header className="page-head">
        <div>
          <h1 className="page-head__title">Leads</h1>
          <p className="page-head__sub">
            Every submission ever captured, newest first. Today is the last day; this is all of them.
          </p>
        </div>
        <span className="live" data-state={connected ? "live" : "offline"}>
          <span className="live__dot" aria-hidden="true" />
          {connected ? "Live" : "Reconnecting"}
        </span>
      </header>

      <Panel flush>
        <div className="filters">
          <div className="filters__search">
            <span className="filters__icon" aria-hidden="true">
              <MagnifyingGlassIcon size={15} />
            </span>
            <input
              className="input"
              type="search"
              placeholder="Search name, email or company"
              aria-label="Search leads by name, email or company"
              value={filters.search}
              onChange={(event) => update({ search: event.target.value })}
            />
          </div>

          <Select
            label="Filter by site"
            value={filters.siteKey}
            options={siteOptions}
            onChange={(siteKey) => update({ siteKey })}
          />
          <Select
            label="Filter by quality"
            value={filters.quality}
            options={QUALITY_OPTIONS}
            onChange={(quality) => update({ quality: quality as LeadFilters["quality"] })}
          />
          <Select
            label="Filter by routing status"
            value={filters.routingStatus}
            options={ROUTING_OPTIONS}
            onChange={(routingStatus) =>
              update({ routingStatus: routingStatus as LeadFilters["routingStatus"] })
            }
          />

          {/* Only offered when there is something to clear, so the bar stays
              quiet in its resting state. */}
          <AnimatePresence initial={false}>
            {anyFilter && (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.15 }}
              >
                <Button
                  variant="quiet"
                  size="sm"
                  icon={<XIcon size={13} weight="bold" />}
                  onClick={() => {
                    setFilters(EMPTY_LEAD_FILTERS);
                    setPage(1);
                    setOpenId(null);
                  }}
                >
                  Clear
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <SkeletonReveal loading={firstLoad} skeleton={<RowSkeleton rows={6} />}>
          {rows.length === 0 ? (
            anyFilter ? (
              <EmptyState
                icon={<MagnifyingGlassIcon size={22} />}
                title="No leads match those filters"
                body="Nothing in the whole history matches. Widen the search or clear a filter to see more."
                action={
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<ArrowCounterClockwiseIcon size={13} weight="bold" />}
                    onClick={() => {
                      setFilters(EMPTY_LEAD_FILTERS);
                      setPage(1);
                    }}
                  >
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<TrayIcon size={22} />}
                title="No leads yet"
                body="Every submission from any of the sites lands here within seconds of the form being sent."
              />
            )
          ) : (
            <div className="records" data-busy={busy}>
              {rows.map((entry) => {
                const { lead } = entry;
                const open = openId === lead.id;
                const status = leadStatus(lead);
                const outcome = deliveryOutcome(entry.deliveries);
                const intent = lead.details.find((detail) => detail.label === INTENT_LABEL);

                return (
                  <div className="lead-row t-acc" data-open={open} key={lead.id}>
                    <button
                      type="button"
                      className="record record--lead"
                      aria-expanded={open}
                      onClick={() => setOpenId(open ? null : lead.id)}
                    >
                      <div className="record__primary">
                        <p className="record__name">{lead.name || "Unnamed enquiry"}</p>
                        <p className="record__meta">
                          {[lead.company, siteName(lead.siteKey)].filter(Boolean).join(" · ")}
                        </p>
                        {intent && (
                          <div className="tags">
                            <span className="tag">
                              <span className="tag__label">{intent.label.toLowerCase()}</span>
                              <span className="tag__value">{intent.value}</span>
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Plain text, not links: this row is a button, and an
                          anchor inside a button is neither valid nor clickable.
                          The mailto and tel links live in the detail panel. */}
                      <div className="record__contact">
                        {lead.email && <span>{lead.email}</span>}
                        {lead.phone && <span>{lead.phone}</span>}
                      </div>

                      <div className="record__aside">
                        <span className="mono" style={{ fontSize: "var(--t-xs)" }}>
                          {listTime(lead.submittedAt)}
                        </span>
                      </div>

                      <div className="record__actions">
                        <Badge tone={status.tone}>{status.label}</Badge>
                        {outcome && <Badge tone={outcome.tone}>{outcome.label}</Badge>}
                      </div>

                      <span className="record__chevron t-acc-chevron">
                        <Chevron size={14} />
                      </span>
                    </button>

                    <div className="t-acc-panel">
                      <div className="t-acc-panel-inner">
                        <LeadDetail entry={entry} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SkeletonReveal>

        {result && result.totalItems > 0 && (
          <Pager
            page={result.page}
            totalPages={result.totalPages}
            totalItems={result.totalItems}
            perPage={result.perPage || LEAD_PAGE_SIZE}
            busy={busy}
            onChange={(next) => {
              setPage(next);
              setOpenId(null);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}
      </Panel>
    </div>
  );
}
